import type { UserInstrumentDto } from "@eu.mdotm.api/sphere";
import { unreachable } from "@mdotm/utils/runtime";
import { BigNumberExt } from "$root/third-party-integrations/bigNumber";
import type { Map as ImmutableMap } from "immutable";
import type { PortfolioLabComposition, PortfolioLabCompositionPortfolio } from "../../../ctx";
import { labSum, weightPrecisionForAum } from "../../../utils/math";
import { classOf } from "../classify";
import { distributeWeightsToTarget, reconcileMonetaryWeights } from "./aum";
import { computeMonetaryWeight } from "./monetary";

/** The holdings a weight edit spreads its new total over. */
export type CoveredHoldings =
	| { kind: "instrument"; tickerName: string }
	| { kind: "direct-instruments" }
	| { kind: "direct-class"; allocationLabel: string }
	| { kind: "portfolio"; portfolioUuid: string }
	| { kind: "class"; allocationLabel: string }
	| { kind: "portfolio-class"; portfolioUuid: string; allocationLabel: string };

export type WeightEditBase = {
	composition: PortfolioLabComposition[];
	instrumentPool: ImmutableMap<string, UserInstrumentDto>;
	groupBy: string;
	aum: number | null;
};

/** A direct instrument (`portfolioUuid: null`) or an instrument nested in a portfolio. */
type Holding = { tickerName: string; portfolioUuid: string | null };
type WeightedHolding = { holding: Holding; weight: number };

export function applyWeightEdit(
	{ composition, instrumentPool, groupBy, aum }: WeightEditBase,
	covered: CoveredHoldings,
	newWeight: number,
): PortfolioLabComposition[] {
	const inClass = (allocationLabel: string, holding: Holding) =>
		classOf(holding.tickerName, instrumentPool, groupBy) === allocationLabel;
	return spreadOverHoldings(
		composition,
		(holding) => isCovered(covered, holding, inClass),
		newWeight,
		aum,
		weightPrecisionForAum(aum),
	);
}

function isCovered(
	covered: CoveredHoldings,
	holding: Holding,
	inClass: (allocationLabel: string, holding: Holding) => boolean,
): boolean {
	const isDirect = holding.portfolioUuid == null;
	switch (covered.kind) {
		case "instrument":
			return isDirect && holding.tickerName === covered.tickerName;
		case "direct-instruments":
			return isDirect;
		case "direct-class":
			return isDirect && inClass(covered.allocationLabel, holding);
		case "portfolio":
			return holding.portfolioUuid === covered.portfolioUuid;
		case "class":
			return inClass(covered.allocationLabel, holding);
		case "portfolio-class":
			return holding.portfolioUuid === covered.portfolioUuid && inClass(covered.allocationLabel, holding);
		default:
			return unreachable(covered);
	}
}

export function scaleAllocationScope(
	composition: PortfolioLabComposition[],
	inAllocation: (ticker: string) => boolean,
	newWeight: number,
	aum: number | null,
	weightPrecision: number,
): PortfolioLabComposition[] {
	return spreadOverHoldings(
		composition,
		(holding) => inAllocation(holding.tickerName),
		newWeight,
		aum,
		weightPrecision,
	);
}

// Selected holdings share `newWeight` in proportion to their current weights (equally when those are
// all zero); a portfolio owning one of them takes the sum of its instruments as its weight.
function spreadOverHoldings(
	composition: PortfolioLabComposition[],
	isSelected: (holding: Holding) => boolean,
	newWeight: number,
	aum: number | null,
	weightPrecision: number,
): PortfolioLabComposition[] {
	const selected = holdingsOf(composition).filter(({ holding }) => isSelected(holding));
	if (selected.length === 0) {
		return composition;
	}
	// Weights are never negative; Normalize asks for the gap to 100 %, which is negative when the
	// unselected groups alone exceed it.
	const spread = distributeWeightsToTarget(
		selected.map(({ weight }) => weight),
		Math.max(0, newWeight),
		weightPrecision,
	);
	const newWeights = new Map(selected.map(({ holding }, i) => [holdingKey(holding), spread[i]]));

	const updated = composition.map((c) => {
		if (c.type === "instrument") {
			const weight = newWeights.get(holdingKey({ tickerName: c.tickerName, portfolioUuid: null }));
			return weight == null ? c : { ...c, weight };
		}
		const instruments = c.instruments.map((i) => {
			const weight = newWeights.get(holdingKey({ tickerName: i.tickerName, portfolioUuid: c.uuid }));
			return weight == null ? i : { ...i, weight };
		});
		if (instruments.every((i, idx) => i === c.instruments[idx])) {
			return c;
		}
		return {
			...c,
			weight: labSum(instruments, (i) => i.weight),
			instruments,
		};
	});
	return reconcileMonetaryWeights(updated, aum);
}

function holdingsOf(composition: PortfolioLabComposition[]): WeightedHolding[] {
	return composition.flatMap((c): WeightedHolding[] =>
		c.type === "instrument"
			? [{ holding: { tickerName: c.tickerName, portfolioUuid: null }, weight: c.weight }]
			: c.instruments.map((i) => ({ holding: { tickerName: i.tickerName, portfolioUuid: c.uuid }, weight: i.weight })),
	);
}

function holdingKey({ tickerName, portfolioUuid }: Holding): string {
	return portfolioUuid == null ? `d:${tickerName}` : `p:${portfolioUuid}:${tickerName}`;
}

// Set a portfolio's outer weight and rescale its inner weights proportionally.
// Inner weights are effective (they sum to the outer weight), so they must scale
// with the outer weight to preserve the sum(inner) === outer invariant.
export function rescalePortfolioOuterWeight(
	portfolio: PortfolioLabCompositionPortfolio,
	newWeight: number,
	aum: number | null,
	weightPrecision: number,
): PortfolioLabCompositionPortfolio {
	// Inner weights are effective (they sum to the outer weight), so distribute onto the AUM grid
	// with an exact sum to the grid-snapped outer weight — preserving sum(inner) === outer exactly
	// (independent per-item rounding would drift the inner sum off the outer weight).
	const gridOuter = BigNumberExt.roundWithPrecision(newWeight, weightPrecision);
	const newInnerWeights = distributeWeightsToTarget(
		portfolio.instruments.map((i) => i.weight),
		gridOuter,
		weightPrecision,
	);

	return {
		...portfolio,
		weight: gridOuter,
		monetaryWeight: computeMonetaryWeight(gridOuter, aum),
		instruments: portfolio.instruments.map((i, idx) => ({
			...i,
			weight: newInnerWeights[idx],
			monetaryWeight: computeMonetaryWeight(newInnerWeights[idx], aum),
		})),
	};
}
